import React, { useContext } from 'react'
import { bool, object, string } from 'prop-types'
import clsx from 'clsx'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { makeStyles } from '@material-ui/core/styles'

// Import Components
import { Advertisement } from '../Advertisement'
import { Breadcrumbs } from '../Breadcrumbs'
import { ContentHeroBannerSubHeader } from '../ContentHeroBanner'
import { ContentSocialBlock } from '../ContentSocialBlock'
import { makeImgResizerUrl } from '@jelly/utils'
import { PencilBanner } from '../PencilBanner'
import { SiteConfigContext } from '../SiteConfigProvider'
import { SponsoredLink } from '../SponsoredLink'
import Typography from '@material-ui/core/Typography'
import { GradientContainer } from '@jelly/ui'

const useStyles = makeStyles(
	(theme) => {
		return {
			BreadcrumbsWrapper: {
				fontSize: '12px',
				margin: 'auto',
				width: '90%',
				[theme.breakpoints.up('sm')]: {
					background: 'none',
					fontSize: '16px',
					margin: 'auto 0 auto 23px',
					right: '0',
					width: '98%',
				},
			},
			HeaderContext: {
				minHeight: '200px',
				flexDirection: 'column',
				width: '100%',
				'&.showOverlappingContent': {
					paddingBottom: '140px',
				},
			},
			HeaderWrapper: {
				width: '100%',
			},
			LinkBar: {
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				height: 'auto',
				width: '100%',
				[theme.breakpoints.up('sm')]: {
					flexDirection: 'row',
					height: '50px',
					background: theme.palette.grey[50],
				},
			},
			LinksWrapper: {
				background: theme.palette.grey[50],
				display: 'flex',
				height: '40px',
				width: '100%',
				[theme.breakpoints.up('sm')]: {
					background: 'none',
					height: 'auto',
				},
			},
			PresentedBy: {
				alignItems: 'center',
				display: 'flex',
				flexWrap: 'wrap',
				justifyContent: 'center',
				padding: '0.5rem',
				'& svg': {
					margin: '.5rem',
				},
			},
			SocialWrapper: {
				display: 'none',
				[theme.breakpoints.up('sm')]: {
					display: 'block',
					margin: 'auto 23px auto 0',
					maxWidth: '150px',
					width: '100%',
					div: {
						justifyContent: 'space-evenly',
					},
				},
			},
			SponsoredContent: {
				color: theme.palette.primary.main,
				margin: '30px auto',
				maxWidth: theme.breakpoints.values.xl,
				textAlign: 'center',
				width: '100%',
			},
			SponsorContext: {
				alignItems: 'center',
				display: 'flex',
				flexDirection: 'column',
				fontSize: '21px',
				justifyContent: 'center',
				margin: '0 auto 16px auto',
				width: '90%',
				svg: {
					margin: '8px',
				},
				[theme.breakpoints.up('md')]: {
					flexDirection: 'row',
				},
			},
			SponsorLogo: {
				display: 'block',
				height: '50px',
				margin: 'auto 0.5rem',
				width: 'auto',
			},
			SubTitle: {
				borderTop: ({ subTitle }) => !!subTitle && `1px solid ${fade(theme.palette.common.white, 0.6)}`,
				paddingBottom: ({ subTitle }) => subTitle && '20px',
				color: ({ isHubTemplate, subTitle }) =>
					isHubTemplate && subTitle ? theme.palette.common.white : fade(theme.palette.common.white, 0.6),
				fontSize: '16px',
				fontWeight: 700,
				margin: 'auto',
				maxWidth: '90%',
				paddingTop: '10px',
				textAlign: 'center',
				[theme.breakpoints.up('sm')]: {
					fontSize: '18px',
				},
				[theme.breakpoints.up('md')]: {
					fontSize: '20px',
					borderTop: 'none',
				},
				[theme.breakpoints.up('xl')]: {
					maxWidth: theme.breakpoints.values.xl,
				},
			},
			StreamText: {
				color: theme.palette.common.white,
				display: 'none',
				fontSize: '16px',
				maxWidth: theme.breakpoints.values.xl,
				textAlign: 'center',
				visibility: 'hidden',
				margin: '16px auto 0 auto',
				width: '90%',
				[theme.breakpoints.up('md')]: {
					display: 'block',
					visibility: 'visible',
				},
			},
			Title: {
				color: theme.palette.common.white,
				fontWeight: 700,
				margin: '0 auto',
				maxWidth: theme.breakpoints.values.xl,
				padding: '30px 0 15px',
				textAlign: 'center',
				width: '90%',
				'&.mbp': {
					color: theme.palette.common.white,
				},
				[theme.breakpoints.up('md')]: {
					paddingBottom: 0,
				},
				'@media (min-width: 1024px)': {
					width: '100%',
				},
			},
		}
	},
	{ name: 'simpleHeader' }
)

SimpleHeader.propTypes = {
	author: object,
	breadcrumbs: object.isRequired,
	classes: object,
	date: string,
	isHubTemplate: bool,
	published: string,
	shouldShowLeaderboardAds: bool,
	showOverlappingContent: bool,
	sponsor: object,
	streamText: string,
	subTitle: string,
	title: string.isRequired,
}
/**
 * This is the Hub Header with the breadcrumb, page title, page subtitle and social sharing buttons
 * Includes breadcrumb schema with the breadcrumb component
 *
 * @param {Object} props.author :: data for the author
 * @param {Object} props.breadcrumbs :: data for the breadcrumbs component (primary and optionary sub)
 * @param {String} props.canonical :: current url path for page
 * @param {String} props.date :: date to display in header
 * @param {Boolean} props.isHubTemplate :: Flag to determine if it is a Hub template of not
 * @param {Object} props.sponsor :: data supplied based on sponsor of the hub
 * @param {Boolean} props.shouldShowLeaderboardAds :: Flag to determine to show leaderboard ads or not
 * @param {Boolean} props.showOverlappingContent :: boolean value to determine if we should add extra height to the banner
 * @param {String} props.streamText :: data to show in header about the page
 * @param {String} props.subTitle :: subtitle of the page
 * @param {String} props.title :: title of the page
 */
export function SimpleHeader({
	author,
	breadcrumbs,
	canonical,
	date,
	isHubTemplate,
	published,
	sponsor,
	shouldShowLeaderboardAds = false,
	showOverlappingContent = false,
	streamText,
	subTitle = null,
	title,
	slug,
}) {
	const isSponsored = !!sponsor
	const sponsoredLogoSrc = isSponsored && makeImgResizerUrl({ src: sponsor.logo, method: 'h', h: '50-png' })
	const {
		siteComponents: {
			HUB_PRESENTED_BY_LOGO: { Svg: SiteLogo, color, height, width },
		},
		siteConstants: {
			META_DATA: { applicationName },
		},
	} = useContext(SiteConfigContext)

	const classes = useStyles()
	const PENCIL_BANNER_SLUGS = ['business-phone-systems', '6780-best-business-phone-systems.html']
	const PENCIL_BANNER_OFFER_URL =
		applicationName === 'business.com'
			? `https://businesscom.go2cloud.org/aff_c?offer_id=87&aff_id=1000&source=business-phone-systems&aff_sub4=revenue|QT&aff_sub3=deal|nextiva&url_id=455`
			: `https://businesscom.go2cloud.org/aff_c?offer_id=87&aff_id=1001&source=business-phone-systems&aff_sub4=revenue|QT&aff_sub3=deal|nextiva&url_id=456`

	return (
		<div className={classes.HeaderWrapper}>
			<div className={classes.LinkBar}>
				<div className={classes.LinksWrapper}>
					<div className={classes.BreadcrumbsWrapper}>
						<Breadcrumbs breadcrumbs={breadcrumbs} />
					</div>
				</div>
				<div className={classes.SocialWrapper}>
					<ContentSocialBlock canonical={canonical} title={title} />
				</div>
			</div>
			{shouldShowLeaderboardAds && !isHubTemplate && <Advertisement isHubTemplate={isHubTemplate} />}
			{PENCIL_BANNER_SLUGS.includes(slug) && <PencilBanner buttonUrl={PENCIL_BANNER_OFFER_URL} />}
			<GradientContainer className={!isHubTemplate && `mbp`}>
				<div
					className={clsx(classes.HeaderContext, {
						showOverlappingContent: showOverlappingContent,
					})}
				>
					<Typography
						className={clsx(classes.Title, {
							mbp: !isHubTemplate,
						})}
						variant="h1"
					>
						{title}
					</Typography>
					{(subTitle || author) && (
						<Typography className={clsx(classes.SubTitle, { mbp: !isHubTemplate })} variant="h2">
							{subTitle || (
								<ContentHeroBannerSubHeader
									authorType="staff" // not using the BE value do to too many author types
									author={author}
									date={date}
									published={published}
								/>
							)}
						</Typography>
					)}
					{streamText && <div className={classes.StreamText}>{streamText}</div>}
				</div>
			</GradientContainer>
			{isSponsored && (
				<div className={classes.SponsoredContent}>
					<div className={classes.SponsorContext}>
						<div className={classes.PresentedBy}>
							Presented by <SiteLogo color={color} height={height} width={width} />
							in Partnership with
						</div>
						<SponsoredLink sponsorName={sponsor.name}>
							<img
								className={clsx(classes.SponsoLogo, 'lazyload')}
								alt={sponsor.name}
								data-src={sponsoredLogoSrc}
							/>
						</SponsoredLink>
					</div>
				</div>
			)}
		</div>
	)
}
